import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Spin } from 'antd'
import styled from 'styled-components'
import { youtubeSearchApi } from '@edulastic/api'
import { loadYouTubeIframeApi } from './youtubeIframeApi'

// Matches watch?v=, youtu.be/, /embed/, etc. and returns the 11-char id.
const extractVideoId = (url = '') => {
  const regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
  const match = url.match(regExp)
  return match && match[2].length === 11 ? match[2] : null
}

// Preserve any campaign/analytics params (utm_*) from the source URL so YouTube
// attribution keeps working — playerVars are serialized onto the embed URL.
const extractUtmParams = (url = '') => {
  const params = {}
  const query = url.split('?')[1]
  if (!query) return params
  new URLSearchParams(query).forEach((value, key) => {
    if (key.startsWith('utm_')) params[key] = value
  })
  return params
}

// Unique DOM id per player instance; YT.Player replaces the target element.
let instanceSeq = 0

/**
 * Drop-in replacement for a raw `<iframe src="youtube.com/embed/...">` that
 * satisfies YouTube's v2 Embedder Identity enforcement. It fetches the encrypted
 * `enc` token from the backend and creates the player via the IFrame API with
 * `embedConfig.enc`, so it keeps working on domains where tokenless embeds are
 * rejected (error 152). Shows a spinner while the token loads and a friendly
 * message if it can't be played.
 */
const TokenizedYouTubePlayer = ({
  url,
  width,
  height,
  playerVars,
  'data-cy': dataCy,
}) => {
  const containerRef = useRef(null)
  const playerRef = useRef(null)
  const idRef = useRef(null)
  if (idRef.current === null) {
    instanceSeq += 1
    idRef.current = `tokenized-yt-player-${instanceSeq}`
  }

  // 'loading' until the token + player are ready, then 'ready' or 'error'.
  const [status, setStatus] = useState('loading')

  useEffect(() => {
    let cancelled = false
    const videoId = extractVideoId(url)
    if (!videoId) {
      setStatus('error')
      return undefined
    }
    setStatus('loading')

    const setup = async () => {
      try {
        const { enc } = await youtubeSearchApi.fetchEmbedToken({ videoId })
        if (cancelled) return
        if (!enc) {
          setStatus('error')
          return
        }
        const YT = await loadYouTubeIframeApi()
        if (cancelled || !containerRef.current) return

        playerRef.current = new YT.Player(idRef.current, {
          videoId,
          width: '100%',
          height: '100%',
          host: 'https://www.youtube.com',
          // utm_* from the source URL override the caller's defaults.
          playerVars: { ...playerVars, ...extractUtmParams(url) },
          embedConfig: { enc },
          events: {
            onReady: () => !cancelled && setStatus('ready'),
            onError: () => !cancelled && setStatus('error'),
          },
        })
      } catch (error) {
        if (!cancelled) setStatus('error')
      }
    }
    setup()

    return () => {
      cancelled = true
      if (playerRef.current?.destroy) {
        playerRef.current.destroy()
      }
      playerRef.current = null
    }
  }, [url])

  return (
    <PlayerFrame ref={containerRef} style={{ width, height }} data-cy={dataCy}>
      <div id={idRef.current} style={{ width: '100%', height: '100%' }} />
      {status !== 'ready' && (
        <Overlay>
          {status === 'error' ? (
            <ErrorText>
              This video is currently unavailable. Please try again later.
            </ErrorText>
          ) : (
            <Spin size="large" />
          )}
        </Overlay>
      )}
    </PlayerFrame>
  )
}

TokenizedYouTubePlayer.propTypes = {
  // YouTube URL in any common form (watch, youtu.be, or embed).
  url: PropTypes.string.isRequired,
  // CSS sizes for the player frame, e.g. '560px' or '100%'.
  width: PropTypes.string,
  height: PropTypes.string,
  playerVars: PropTypes.object,
  'data-cy': PropTypes.string,
}

TokenizedYouTubePlayer.defaultProps = {
  width: '100%',
  height: '100%',
  playerVars: undefined,
  'data-cy': undefined,
}

export default TokenizedYouTubePlayer

const PlayerFrame = styled.div`
  position: relative;
  margin: auto;
`

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000000;
`

const ErrorText = styled.span`
  color: #ffffff;
  text-align: center;
  padding: 0 20px;
`
